import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, MapPinOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { getPosition } from "@/lib/geo";
import { useAuth } from "@/context/AuthContext";

const PING_INTERVAL_MS = 3 * 60 * 1000;
const GPS_FAIL_LIMIT = 2; // consecutive failed readings before the app blocks (avoids false blocks on brief signal loss)

const GATED_ROLES = ["travel_trainer", "admin_manager"];

function coordsOf(pos) {
  return { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
}

export default function ShiftTracker() {
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const failCountRef = useRef(0);

  const sendPing = useCallback(async () => {
    try {
      const pos = await getPosition();
      await api.post("/attendance/location", coordsOf(pos));
      if (failCountRef.current >= GPS_FAIL_LIMIT) window.dispatchEvent(new Event("gps-restored"));
      failCountRef.current = 0;
    } catch (err) {
      if (err?.response?.status === 409) {
        // Shift was auto-ended server-side (inactivity) — resync and tell the user
        setShift(null);
        window.dispatchEvent(new Event("shift-changed"));
        return;
      }
      failCountRef.current += 1;
      // GPS turned off mid-shift (or permission revoked): force gated roles to re-enable before continuing
      if (failCountRef.current >= GPS_FAIL_LIMIT && GATED_ROLES.includes(user?.role)) {
        window.dispatchEvent(new Event("gps-lost"));
        api.post("/attendance/gps-lost").catch(() => {}); // alert the office (deduped server-side)
      }
    }
  }, [user?.role]);

  useEffect(() => {
    const fetchStatus = () => api.get("/attendance/status").then((r) => {
      const next = r.data.shift;
      setShift((prev) => {
        if (prev && !next) toast.warning("Your shift was ended automatically after 3 hours of inactivity. Start a new shift if you are still working.");
        return next;
      });
    }).catch(() => {});
    fetchStatus();
    const poll = setInterval(fetchStatus, 5 * 60 * 1000);
    window.addEventListener("shift-changed", fetchStatus);
    return () => {
      clearInterval(poll);
      window.removeEventListener("shift-changed", fetchStatus);
    };
  }, []);

  useEffect(() => {
    if (!shift) return undefined;
    sendPing();
    timerRef.current = setInterval(sendPing, PING_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [shift, sendPing]);

  const signIn = async () => {
    setBusy(true);
    let coords = {};
    try {
      coords = coordsOf(await getPosition());
    } catch {
      if (GATED_ROLES.includes(user?.role)) {
        toast.error("Location is required to start your shift. Enable location services for this site, then try again.");
        setBusy(false);
        return;
      }
      toast.warning("Location unavailable — signing in without GPS. Allow location access so the office can see your position.");
    }
    try {
      const { data } = await api.post("/attendance/sign-in", coords);
      setShift(data.shift);
      window.dispatchEvent(new Event("shift-changed"));
      toast.success("Shift started — the Co-ordinator and Manager have been alerted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    setBusy(true);
    let coords = {};
    try {
      coords = coordsOf(await getPosition());
    } catch {
      /* sign-out still allowed without GPS */
    }
    try {
      await api.post("/attendance/sign-out", coords);
      setShift(null);
      window.dispatchEvent(new Event("shift-changed"));
      toast.success("Shift ended — the Co-ordinator and Manager have been alerted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const since = (() => {
    if (!shift) return "";
    try {
      return new Date(shift.signed_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  })();

  return (
    <div className="mb-3 rounded-lg border border-slate-700 bg-white/5 p-3" data-testid="shift-tracker">
      <div className="flex items-center gap-2 text-xs text-slate-300">
        {shift ? (
          <MapPin className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
        ) : (
          <MapPinOff className="h-3.5 w-3.5 shrink-0 text-slate-500" />
        )}
        <span data-testid="shift-status-text">{shift ? `On shift since ${since}` : "Off shift"}</span>
      </div>
      {shift && (
        <p className="mt-1 text-[10px] leading-snug text-slate-500" data-testid="shift-tracking-notice">
          Your GPS position is shared with the office while you are on shift.
        </p>
      )}
      <button
        data-testid={shift ? "shift-sign-out-button" : "shift-sign-in-button"}
        onClick={shift ? signOut : signIn}
        disabled={busy}
        className={`mt-2 w-full rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 min-h-[44px] disabled:opacity-60 ${
          shift
            ? "bg-red-600/20 text-red-300 hover:bg-red-600/30 ring-1 ring-red-500/40"
            : "bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30 ring-1 ring-emerald-500/40"
        }`}
      >
        {busy ? "Please wait…" : shift ? "End shift" : "Start shift"}
      </button>
    </div>
  );
}
